import { Component, inject, Input, OnInit } from '@angular/core';
import { DataTableSchema, DefaultTableSchema, TableColumnSchema, TITLE_TYPE } from '../../../model/dynamic-data-table.model';
import { DynamicFormArray } from '../../../model/form.model';
import { SearchConfig } from '../../../model/api.model';
import { surveyResponsesApiConfig } from '../../../model/survey.model';
import { CommonService } from '../../../services/common.service';
import { FilterListContainerComponent } from '../../../layout/filter-list-container/filter-list-container.component';

type QuestionColumn = { key: string; question: string };

const BASE_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'spinId', label: 'Spin ID' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'jobRole', label: 'Job Role' },
  { key: 'dealership', label: 'Dealership' },
  { key: 'dealer_code', label: 'Dealer Code' },
  { key: 'area_district', label: 'Area/District' },
];

@Component({
  selector: 'app-survey-attendee-table',
  standalone: true,
  imports: [FilterListContainerComponent],
  templateUrl: './survey-attendee-table.component.html',
  styleUrl: './survey-attendee-table.component.scss',
})
export class SurveyAttendeeTableComponent implements OnInit {
  private readonly commonService = inject(CommonService);
  @Input() apiConfig: SearchConfig = surveyResponsesApiConfig;
  @Input() sectionTitle = 'All Attendee Responses';
  @Input() elementId = 'SurveyAttendees';

  readonly filterSchema: DynamicFormArray = { FieldsArray: [], ButtonsArray: [], title: '', autoSearchOnLoad: false };
  tableSchema: DataTableSchema = this.buildSchema([], []);

  ngOnInit(): void {
    this.tableSchema = { 
      ...this.tableSchema, 
      isLoading: true 
    };
    this.commonService.getDataFromDataSource(this.apiConfig, this.apiConfig.urlPath ?? '').then((response: any) => {
        const payload = response?.payload ?? response?.result ?? [];
        this.applyPayload(Array.isArray(payload) ? payload : []);
      })
      .catch(() => {
        this.applyPayload([]);
      });
  }

  private applyPayload(payload: any[]): void {
    const questionColumns = this.extractQuestionColumns(payload);
    const rows = payload.map(item => this.mapRow(item, questionColumns));
    this.tableSchema = this.buildSchema(rows, questionColumns);
  }

  private extractQuestionColumns(payload: any[]): QuestionColumn[] {
    const seen = new Map<string, QuestionColumn>();
    let counter = 0;
    for (const item of payload) {
      const responses = Array.isArray(item?.transformed_responses) ? item.transformed_responses : [];
      for (const response of responses) {
        const questionText = String(response?.question ?? '').trim();
        if (!questionText) { continue; }
        const dedupeKey = questionText.toLowerCase();
        if (!seen.has(dedupeKey)) {
          counter += 1;
          seen.set(dedupeKey, { key: `q${counter}`, question: questionText });
        }
      }
    }
    return Array.from(seen.values());
  }

  private mapRow(item: any, questionColumns: QuestionColumn[]): Record<string, any> {
    const responses = Array.isArray(item?.transformed_responses) ? item.transformed_responses : [];
    const answersByQuestion = new Map<string, string>();
    for (const response of responses) {
      const questionText = String(response?.question ?? '').trim().toLowerCase();
      if (!questionText) { continue; }
      answersByQuestion.set(questionText, response?.answer ?? '');
    }
    const row: Record<string, any> = {
      spinId: item?.spin_id ?? '',
      firstName: item?.first_name ?? '',
      lastName: item?.last_name ?? '',
      email: item?.email ?? '',
      dealership: item?.dealership ?? '',
      area_district: item?.area_district ?? '',
      dealer_code: item?.dealer_code ?? '',
      jobRole: item?.job_role ?? '',
      district: item?.district ?? '',
    };
    for (const column of questionColumns) {
      row[column.key] = answersByQuestion.get(column.question.toLowerCase()) ?? '';
    }
    return row;
  }

  private buildSchema(rows: Record<string, any>[], questionColumns: QuestionColumn[]): DataTableSchema {
    const columnSchema: TableColumnSchema[] = [
      ...BASE_COLUMNS.map(column => ({ label: column.label, fieldKey: column.key, isSortable: true })),
      ...questionColumns.map(question => ({ label: question.question, fieldKey: question.key, isSortable: true, tooltip: question.question })),
    ];
    const exportSchema: Record<string, string> = {};
    for (const column of BASE_COLUMNS) { exportSchema[column.key] = column.label; }
    for (const question of questionColumns) { exportSchema[question.key] = question.question; }
    return {
      ...structuredClone(DefaultTableSchema),
      titleSchema: { label: '', title: this.sectionTitle, type: TITLE_TYPE.Static },
      title: this.sectionTitle,
      columnSchema,
      displayedColumns: columnSchema.map(column => column.fieldKey),
      dataSource: rows,
      isPaginationRequired: true,
      isSearchRequired: true,
      isSortRequired: true,
      isExportRequired: true,
      isFilterListExportRequired: false,
      isLoading: false,
      activeSorts: [],
      exportConfig: { fileName: 'All_Attendee_Responses', exportMessage: this.sectionTitle, schema: exportSchema },
    };
  }
}
